import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Modal, Form, Row, Col, Badge, Alert, Accordion } from 'react-bootstrap';
import { FaFlask, FaPlus, FaEye, FaEdit, FaTrash, FaCamera, FaFileAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';

const QualityTestingTab = ({ salesOrderId, salesOrderItems, onQCChange }) => {
  const [qcTests, setQcTests] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [editingTest, setEditingTest] = useState(null);
  const [formData, setFormData] = useState({
    lab: '',
    sampleCollectionDate: '',
    remarks: '',
    reportDate: '',
    confirmationMode: 'Pending',
    result: 'Pending',
    itemWiseQCStatus: []
  });
  const [files, setFiles] = useState({
    samplePhoto: null,
    stackPhoto: null,
    reportFile: null
  });

  useEffect(() => {
    // Initialize item-wise QC status when sales order items change
    if (salesOrderItems && salesOrderItems.length > 0) {
      const initialQCStatus = salesOrderItems.map(item => ({
        salesOrderItem: item._id,
        qcStatus: 'Pending',
        remarks: ''
      }));
      setFormData(prev => ({
        ...prev,
        itemWiseQCStatus: initialQCStatus
      }));
    }
  }, [salesOrderItems]);

  const fetchQCTests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/qc-testing/${salesOrderId}`);
      setQcTests(response.data);
    } catch (error) {
      toast.error('Error fetching QC tests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  },[salesOrderId]);

  useEffect(() => {
    if (salesOrderId) {
      fetchQCTests();
    }
    fetchLabs();
  }, [salesOrderId, fetchQCTests]);

  const fetchLabs = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/labs?isActive=true`);
      setLabs(response.data);
    } catch (error) {
      console.error('Error fetching labs:', error);
      toast.error('Failed to load labs');
    }
  };

  const handleCreateTest = async () => {
    try {
      const formDataToSend = new FormData();

      // Add basic fields
      Object.keys(formData).forEach(key => {
        if (key === 'itemWiseQCStatus') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      formDataToSend.append('salesOrderId', salesOrderId);

      // Add files
      if (files.samplePhoto) formDataToSend.append('samplePhoto', files.samplePhoto);
      if (files.stackPhoto) formDataToSend.append('stackPhoto', files.stackPhoto);
      if (files.reportFile) formDataToSend.append('reportFile', files.reportFile);

      await axios.post(`${config.API_BASE_URL}/qc-testing`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('QC test record created successfully');
      setShowCreateModal(false);
      resetForm();
      await fetchQCTests();
      if (onQCChange) onQCChange();
    } catch (error) {
      toast.error('Error creating QC test record');
      console.error(error);
    }
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    setFormData({
      lab: test.lab?._id || test.lab,
      sampleCollectionDate: test.sampleCollectionDate ? new Date(test.sampleCollectionDate).toISOString().split('T')[0] : '',
      remarks: test.remarks || '',
      reportDate: test.reportDate ? new Date(test.reportDate).toISOString().split('T')[0] : '',
      confirmationMode: test.confirmationMode || 'Report received',
      result: test.result || 'Pass',
      itemWiseQCStatus: test.itemWiseQCStatus || []
    });
    setShowEditModal(true);
  };

  const handleUpdateTest = async () => {
    try {
      const formDataToSend = new FormData();

      // Add basic fields
      Object.keys(formData).forEach(key => {
        if (key === 'itemWiseQCStatus') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add files
      if (files.samplePhoto) formDataToSend.append('samplePhoto', files.samplePhoto);
      if (files.stackPhoto) formDataToSend.append('stackPhoto', files.stackPhoto);
      if (files.reportFile) formDataToSend.append('reportFile', files.reportFile);

      await axios.patch(`${config.API_BASE_URL}/qc-testing/${editingTest._id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('QC test record updated successfully');
      setShowEditModal(false);
      resetForm();
      await fetchQCTests();
      if (onQCChange) onQCChange();
    } catch (error) {
      toast.error('Error updating QC test record');
      console.error(error);
    }
  };

  const handleViewTest = (test) => {
    setSelectedTest(test);
    setShowViewModal(true);
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this QC test record?')) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/qc-testing/${testId}`);
      toast.success('QC test record deleted successfully');
      await fetchQCTests();
      if (onQCChange) onQCChange();
    } catch (error) {
      toast.error('Error deleting QC test record');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      lab: '',
      sampleCollectionDate: '',
      remarks: '',
      reportDate: '',
      confirmationMode: 'Pending',
      result: 'Pending',
      itemWiseQCStatus: salesOrderItems?.map(item => ({
        salesOrderItem: item._id,
        qcStatus: 'Pending',
        remarks: ''
      })) || []
    });
    setFiles({
      samplePhoto: null,
      stackPhoto: null,
      reportFile: null
    });
  };

  const handleFileChange = (field, file) => {
    setFiles(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleQCStatusChange = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      itemWiseQCStatus: prev.itemWiseQCStatus.map(status =>
        status.salesOrderItem === itemId ? { ...status, [field]: value } : status
      )
    }));
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Pending': 'secondary',
      'Sample Collected': 'warning',
      'Passed': 'success',
      'Failed': 'danger',
      'Rejected': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getResultBadge = (result) => {
    return result === 'Pass' ?
      <Badge bg="success"><FaCheckCircle className="me-1" />Pass</Badge> :
      <Badge bg="danger"><FaTimesCircle className="me-1" />Fail</Badge>;
  };

  const handleViewFile = (filePath) => {
    if (filePath) {
      window.open(filePath, '_blank');
    }
  };

  if (loading) {
    return <div className="text-center py-3">Loading QC tests...</div>;
  }

  return (
    <>
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-gradient-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFlask className="me-2" />
              QC & Lab Testing
            </h5>
            <Button
              variant="light"
              onClick={() => setShowCreateModal(true)}
            >
              <FaPlus className="me-1" />
              Add QC Test
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {qcTests.length === 0 ? (
            <Alert variant="info">
              <FaFlask className="me-2" />
              No QC testing records found. Click "Add QC Test" to create the first record.
            </Alert>
          ) : (
            <Accordion defaultActiveKey="0" className="mb-3">
              {qcTests.map((test, index) => (
                <Accordion.Item eventKey={index.toString()} key={test._id}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                      <div>
                        <h6 className="mb-0">
                          <FaFlask className="me-2 text-primary" />
                          {test.lab?.name || test.labName || 'N/A'}
                        </h6>
                        <small className="text-muted">
                          Sample Date: {new Date(test.sampleCollectionDate).toLocaleDateString()}
                          {test.reportDate && ` | Report Date: ${new Date(test.reportDate).toLocaleDateString()}`}
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {getResultBadge(test.result)}
                        <Badge bg="info">{test.confirmationMode}</Badge>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTest(test);
                            }}
                            title="Edit"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTest(test);
                            }}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTest(test._id);
                            }}
                            title="Delete"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="p-3 bg-light rounded">
                          <h6>Lab Information</h6>
                          <p><strong>Lab Name:</strong> {test.labName}</p>
                          <p><strong>Sample Collection:</strong> {new Date(test.sampleCollectionDate).toLocaleDateString()}</p>
                          {/* <p><strong>Created By:</strong> {test.createdBy?.name || 'N/A'}</p> */}
                          <p><strong>Created At:</strong> {new Date(test.createdAt).toLocaleString()}</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="p-3 bg-light rounded">
                          <h6>Report Information</h6>
                          <p><strong>Confirmation Mode:</strong> {test.confirmationMode}</p>
                          <p><strong>Result:</strong> {getResultBadge(test.result)}</p>
                          {test.reportDate && (
                            <p><strong>Report Date:</strong> {new Date(test.reportDate).toLocaleDateString()}</p>
                          )}
                          {test.remarks && (
                            <p><strong>Remarks:</strong> {test.remarks}</p>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Files Section */}
                    {(test.samplePhoto || test.stackPhoto || test.reportFile) && (
                      <Row className="mt-3">
                        <Col md={12}>
                          <h6>Uploaded Files</h6>
                          <div className="d-flex gap-2 flex-wrap">
                            {test.samplePhoto && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewFile(test.samplePhoto)}
                              >
                                <FaCamera className="me-1" />
                                Sample Photo
                              </Button>
                            )}
                            {test.stackPhoto && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewFile(test.stackPhoto)}
                              >
                                <FaCamera className="me-1" />
                                Stack Photo
                              </Button>
                            )}
                            {test.reportFile && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleViewFile(test.reportFile)}
                              >
                                <FaFileAlt className="me-1" />
                                Report File
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Item-wise QC Status */}
                    <Row className="mt-3">
                      <Col md={12}>
                        <h6>Item-wise QC Status</h6>
                        <Table bordered size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Product</th>
                              <th>Quality</th>
                              <th>Quantity</th>
                              <th>QC Status</th>
                              <th>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {test.itemWiseQCStatus?.map((status, idx) => {
                              const item = status.salesOrderItem; // This is now populated from backend
                              return (
                                <tr key={idx}>
                                  <td>{item?.product?.productName || 'N/A'}</td>
                                  <td>{item?.quality || '-'}</td>
                                  <td>{item?.quantity} {item?.unit}</td>
                                  <td>{getStatusBadge(status.qcStatus)}</td>
                                  <td>{status.remarks || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Card.Body>
      </Card>

      {/* Create QC Test Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFlask className="me-2" />
            Add QC Test Record
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Lab *</Form.Label>
                  <Form.Select
                    value={formData.lab}
                    onChange={(e) => setFormData(prev => ({ ...prev, lab: e.target.value }))}
                    required
                  >
                    <option value="">Select a lab</option>
                    {labs.map(lab => (
                      <option key={lab._id} value={lab._id}>
                        {lab.name} - {lab.address}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Sample Collection Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.sampleCollectionDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, sampleCollectionDate: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Report Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.reportDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, reportDate: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Confirmation Mode *</Form.Label>
                  <Form.Select
                    value={formData.confirmationMode}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmationMode: e.target.value }))}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Report received">Report received</option>
                    <option value="Client side confirmation">Client side confirmation</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Result *</Form.Label>
                  <Form.Select
                    value={formData.result}
                    onChange={(e) => setFormData(prev => ({ ...prev, result: e.target.value }))}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Sample Collected">Sample Collected</option>
                    <option value="Pass">Pass</option>
                    <option value="Fail">Fail</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Optional remarks"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* File Uploads */}
            <Row className="g-3 mt-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Sample Photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('samplePhoto', e.target.files[0])}
                  />
                  <Form.Text className="text-muted">Upload sample photo</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Stack Photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('stackPhoto', e.target.files[0])}
                  />
                  <Form.Text className="text-muted">Upload stack photo</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Report File</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange('reportFile', e.target.files[0])}
                  />
                  <Form.Text className="text-muted">Upload report file</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Item-wise QC Status */}
            <Row className="mt-4">
              <Col md={12}>
                <h6>Item-wise QC Status</h6>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Product</th>
                      <th>Quality</th>
                      <th>Quantity</th>
                      <th>QC Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesOrderItems?.map((item) => {
                      const qcStatus = formData.itemWiseQCStatus.find(
                        status => status.salesOrderItem === item._id
                      );
                      return (
                        <tr key={item._id}>
                          <td>{item.product?.productName}</td>
                          <td>{item.quality}</td>
                          <td>{item.quantity} {item.unit}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={qcStatus?.qcStatus || 'Pending'}
                              onChange={(e) => handleQCStatusChange(item._id, 'qcStatus', e.target.value)}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Sample Collected">Sample Collected</option>
                              <option value="Passed">Passed</option>
                              <option value="Failed">Failed</option>
                              {/* <option value="Rejected">Rejected</option> */}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              as="textarea"
                              rows={1}
                              size="sm"
                              value={qcStatus?.remarks || ''}
                              onChange={(e) => handleQCStatusChange(item._id, 'remarks', e.target.value)}
                              placeholder="Remarks"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateTest}
            disabled={!formData.lab || !formData.sampleCollectionDate}
          >
            Create QC Test Record
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit QC Test Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit className="me-2" />
            Edit QC Test Record
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Lab *</Form.Label>
                  <Form.Select
                    value={formData.lab}
                    onChange={(e) => setFormData(prev => ({ ...prev, lab: e.target.value }))}
                    required
                  >
                    <option value="">Select a lab</option>
                    {labs.map(lab => (
                      <option key={lab._id} value={lab._id}>
                        {lab.name} - {lab.address}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Sample Collection Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.sampleCollectionDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, sampleCollectionDate: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Report Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.reportDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, reportDate: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Confirmation Mode *</Form.Label>
                  <Form.Select
                    value={formData.confirmationMode}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmationMode: e.target.value }))}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Report received">Report received</option>
                    <option value="Client side confirmation">Client side confirmation</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Result *</Form.Label>
                  <Form.Select
                    value={formData.result}
                    onChange={(e) => setFormData(prev => ({ ...prev, result: e.target.value }))}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Sample Collected">Sample Collected</option>
                    <option value="Pass">Pass</option>
                    <option value="Fail">Fail</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Optional remarks"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* File Uploads */}
            <Row className="g-3 mt-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Sample Photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('samplePhoto', e.target.files[0])}
                  />
                  <Form.Text className="text-muted">
                    {editingTest?.samplePhoto ? 'Upload new sample photo to replace existing' : 'Upload sample photo'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Stack Photo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('stackPhoto', e.target.files[0])}
                  />
                  <Form.Text className="text-muted">
                    {editingTest?.stackPhoto ? 'Upload new stack photo to replace existing' : 'Upload stack photo'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Report File</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange('reportFile', e.target.files[0])}
                  />
                  <Form.Text className="text-muted">
                    {editingTest?.reportFile ? 'Upload new report file to replace existing' : 'Upload report file'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Item-wise QC Status */}
            <Row className="mt-4">
              <Col md={12}>
                <h6>Item-wise QC Status</h6>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Product</th>
                      <th>Quality</th>
                      <th>Quantity</th>
                      <th>QC Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesOrderItems?.map((item) => {
                      const qcStatus = formData.itemWiseQCStatus.find(
                        status => status.salesOrderItem === item._id
                      );
                      return (
                        <tr key={item._id}>
                          <td>{item.product?.productName}</td>
                          <td>{item.quality}</td>
                          <td>{item.quantity} {item.unit}</td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={qcStatus?.qcStatus || 'Pending'}
                              onChange={(e) => handleQCStatusChange(item._id, 'qcStatus', e.target.value)}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Sample Collected">Sample Collected</option>
                              <option value="Passed">Passed</option>
                              <option value="Failed">Failed</option>
                              {/* <option value="Rejected">Rejected</option> */}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              as="textarea"
                              rows={1}
                              size="sm"
                              value={qcStatus?.remarks || ''}
                              onChange={(e) => handleQCStatusChange(item._id, 'remarks', e.target.value)}
                              placeholder="Remarks"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateTest}
            disabled={!formData.lab || !formData.sampleCollectionDate}
          >
            Update QC Test Record
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View QC Test Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFlask className="me-2" />
            QC Test Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTest && (
            <>
              <Row className="g-3">
                <Col md={6}>
                  <div className="p-3 bg-light rounded">
                    <h6>Lab Information</h6>
                    <p><strong>Lab Name:</strong> {selectedTest.lab?.name || selectedTest.labName || 'N/A'}</p>
                    <p><strong>Sample Collection:</strong> {new Date(selectedTest.sampleCollectionDate).toLocaleDateString()}</p>
                    {/* <p><strong>Created By:</strong> {selectedTest.createdBy?.name || 'N/A'}</p> */}
                    <p><strong>Created At:</strong> {new Date(selectedTest.createdAt).toLocaleString()}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 bg-light rounded">
                    <h6>Report Information</h6>
                    <p><strong>Confirmation Mode:</strong> {selectedTest.confirmationMode}</p>
                    <p><strong>Result:</strong> {getResultBadge(selectedTest.result)}</p>
                    {selectedTest.reportDate && (
                      <p><strong>Report Date:</strong> {new Date(selectedTest.reportDate).toLocaleDateString()}</p>
                    )}
                    {selectedTest.remarks && (
                      <p><strong>Remarks:</strong> {selectedTest.remarks}</p>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Files Section */}
              {(selectedTest.samplePhoto || selectedTest.stackPhoto || selectedTest.reportFile) && (
                <Row className="mt-3">
                  <Col md={12}>
                    <h6>Uploaded Files</h6>
                    <div className="d-flex gap-2 flex-wrap">
                      {selectedTest.samplePhoto && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewFile(selectedTest.samplePhoto)}
                        >
                          <FaCamera className="me-1" />
                          Sample Photo
                        </Button>
                      )}
                      {selectedTest.stackPhoto && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewFile(selectedTest.stackPhoto)}
                        >
                          <FaCamera className="me-1" />
                          Stack Photo
                        </Button>
                      )}
                      {selectedTest.reportFile && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleViewFile(selectedTest.reportFile)}
                        >
                          <FaFileAlt className="me-1" />
                          Report File
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              )}

              {/* Item-wise QC Status */}
              <Row className="mt-3">
                <Col md={12}>
                  <h6>Item-wise QC Status</h6>
                  <Table bordered size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>Product</th>
                        <th>Quality</th>
                        <th>Quantity</th>
                        <th>QC Status</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTest.itemWiseQCStatus?.map((status, idx) => {
                        const item = status.salesOrderItem; // This is now populated from backend
                        return (
                          <tr key={idx}>
                            <td>{item?.product?.productName || 'N/A'}</td>
                            <td>{item?.quality || '-'}</td>
                            <td>{item?.quantity} {item?.unit}</td>
                            <td>{getStatusBadge(status.qcStatus)}</td>
                            <td>{status.remarks || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QualityTestingTab;
